<template>
  <div>
    <!-- Alerts -->
    <div class="container-fluid">
      <div class="mt-4">
        <AlertBannerRecords path="/companies" :message="companiesMessages" />
      </div>
      <FutureAlert :data="futureAlertData" :loading="futureAlertData.loading" />
    </div>

    <!-- Filtros de Período -->
    <div class="container-fluid">
      <div class="filters-card">
        <div class="row">
          <div class="col-md-12 mb-3">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">
                <filter-icon size="1.2x" class="mr-2" />
                <strong>Filtros</strong>
              </h5>
              <button
                class="btn btn-link btn-sm"
                @click="toggleFilters"
                type="button"
              >
                {{ showAdvancedFilters ? 'Ocultar filtros avançados' : 'Mostrar filtros avançados' }}
                <chevron-down-icon v-if="!showAdvancedFilters" size="1x" />
                <chevron-up-icon v-else size="1x" />
              </button>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-2">
            <label class="mr-2 nowrap" for="period-type">
              Tipo de período
              <info-icon size="1x"
                v-tooltip.top="'Alguns órgãos podem não fornecer a data da ocorrência e estas multas não serão incluídas nos gráficos ao pesquisar por este tipo de período. ' +
                  'Para garantir a visualização de todas as multas, acesse o aplicativo e faça uma busca pelo status da multa'" />
            </label>

            <select v-model="periodType" id="period-type" class="form-control">
              <option value="updated_at">Data de atualização</option>
              <option value="created_at">Data de criação</option>
              <option value="ocorrido_em">Data da infração</option>
              <option value="vencimento_em">Data de vencimento</option>
              <option value="boleto_vencimento_em">Data de vencimento do boleto</option>
            </select>
          </div>

          <div class="col-md-2">
            <label for="start-date">Data inicial</label>
            <input v-model="startDate" type="date" id="start-date" class="form-control">
          </div>

          <div class="col-md-2">
            <label for="end-date">Data final</label>
            <input v-model="endDate" type="date" id="end-date" class="form-control">
          </div>

          <div class="col-md-2">
            <label for="type-value">
              Exibir valor por
              <info-icon size="1x" v-tooltip.top="{ content: labelValueDescription, class: 'tooltip-with-linebreaks' }" />
            </label>
            <select v-model="valueType" id="type-value" class="form-control" @change="applyFilters">
              <option value="valor_estimado_cents">Valor estimado</option>
              <option value="valor_cents">Valor da multa</option>
              <option value="boleto.valor_cents">Valor do boleto</option>
            </select>
          </div>

          <div class="col-md-2">
            <label for="company-status">Status da Empresa</label>
            <select v-model="companyStatus" id="company-status" class="form-control" @change="applyFilters">
              <option v-for="[label, value] in companyStatuses" :key="value" :value="value">
                {{ label }}
              </option>
            </select>
          </div>

          <div class="col-md-2 filters-card-actions">
            <button @click="applyFilters" class="btn btn-primary" :disabled="isLoadingData">
              <i class="fas fa-search"></i>
              {{ isLoadingData ? 'Carregando...' : 'Aplicar Filtros' }}
            </button>
            <router-link tag="button" :to="appLink" class="btn btn-primary">
              Acessar aplicativo
            </router-link>
          </div>
        </div>

        <transition name="slide-fade">
          <div v-if="showAdvancedFilters" class="row mt-3 pt-3 border-top">
            <div class="col-md-4">
              <label for="filter-status">
                Status da multa
              </label>
              <Select2
                id="filter-status"
                v-model="selectedStatus"
                :options="availableStatusOptions"
              />
            </div>

            <div class="col-md-4">
              <label for="filter-autuador">
                Órgão autuador
              </label>
              <Select2
                id="filter-autuador"
                v-model="selectedAutuador"
                :options="autuadorOptionsForSelect"
              />
            </div>

            <div class="col-md-4">
              <label for="filter-estado">
                Estado (UF)
              </label>
              <Select2
                id="filter-estado"
                v-model="selectedEstados"
                :options="estadosOptions"
              />
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Cards -->
    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col-xl-4 col-lg-4 col-md-6 mb-3" v-for="status in statusCardsData.list" :key="status.title">
          <Card :data="status" :loading="statusCardsData.loading" data-sum :link="status.link" />
        </div>
      </div>
    </div>

    <!-- Charts -->
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-6 col-md-12">
          <div class="mt-3">
            <CmEmbarcadorDescricaoChart
              graph-title="Multas por Descrição"
              :data="situationChartData.list"
              :loading="situationChartData.loading" />
          </div>
        </div>

        <div class="col-lg-6 col-md-12">
          <div class="mt-3">
            <CmEmbarcadorAutuadorChart
              graph-title="Órgão Autuador"
              :graph-data="autuadorChartData.list"
              :loading="autuadorChartData.loading" />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-6 col-md-12">
          <div class="mt-3">
            <CmEmbarcadorEstadoChart
              graph-title="Mapa de Calor do Brasil"
              :graph-data="estadoChartData.list"
              :loading="estadoChartData.loading" />
          </div>
        </div>

        <div class="col-lg-6 col-md-12">
          <div class="mt-3">
            <CmEmbarcadorRiskMatrixChart
              graph-title="Matriz de Risco por CNPJ"
              :graph-data="riskMatrixData.list"
              :loading="riskMatrixData.loading" />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="mt-3">
            <CmEmbarcadorTimelineChart
              graph-title="Infrações por Mês"
              :data="timelineChartData.list"
              :loading="timelineChartData.loading" />
          </div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="container-fluid mt-4">
      <div class="row">
        <div class="col-md-12 mb-3">
          <h5 class="d-flex align-items-center">
            <i class="fas fa-lightbulb mr-2 text-warning"></i>
            <strong>Insights</strong>
          </h5>
        </div>

        <div class="col-lg-4 col-md-6">
          <InsightCard
            title="Média por Multa"
            :value="insightsData.avgValue"
            description="Valor médio de cada multa"
            :loading="insightsData.loading"
            variant="info"
            icon="fas fa-dollar-sign"
            type="currency" />
        </div>

        <div class="col-lg-4 col-md-6">
          <InsightCard
            title="Maior Autuador"
            :value="insightsData.topAutuador"
            :description="`${insightsData.topAutuadorCount} multas`"
            :loading="insightsData.loading"
            variant="warning"
            icon="fas fa-exclamation-triangle"
            type="text" />
        </div>

        <div class="col-lg-4 col-md-6">
          <InsightCard
            title="Estado com Mais Multas"
            :value="insightsData.topEstado"
            :description="`${insightsData.topEstadoCount} multas`"
            :loading="insightsData.loading"
            variant="danger"
            icon="fas fa-map-marker-alt"
            type="text" />
        </div>
      </div>
    </div>

    <!-- Top CNPJs -->
    <div class="container-fluid mt-4">
      <div class="row">
        <div class="col-md-12 mt-3 mb-1">
          <h5 class="d-flex align-items-center">
            <alert-triangle-icon size="1.3x" class="text-warning mr-2" />
            <strong>Top CNPJs com mais multas</strong>
          </h5>
        </div>

        <div class="col-md-12">
          <Companies :data="topCnpjsData.list" :loading="topCnpjsData.loading" :start-date="startDate"
            :end-date="endDate" :period-type="periodType" :company-status="companyStatus" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GetAccount from '@/mixins/GetAccount'
import GetAccountApps from '@/mixins/GetAccountApps'

import Card from '@/components/dashboard/dashboard_cm_embarcador/Card.vue'
import Companies from '@/components/dashboard/dashboard_cm_embarcador/Companies.vue'

import CmEmbarcadorAutuadorChart from '@/components/charts/CmEmbarcadorAutuadorChart.vue';
import CmEmbarcadorDescricaoChart from '@/components/charts/CmEmbarcadorDescricaoChart.vue';
import CmEmbarcadorEstadoChart from '@/components/charts/CmEmbarcadorEstadoChart.vue';
import CmEmbarcadorRiskMatrixChart from '@/components/charts/CmEmbarcadorRiskMatrixChart.vue';
import CmEmbarcadorTimelineChart from '@/components/charts/CmEmbarcadorTimelineChart.vue';

import AlertBannerRecords from '@/components/shares/AlertBannerRecords.vue';
import FutureAlert from '@/components/dashboard/cards/FutureAlert.vue'
import InsightCard from '@/components/dashboard/cards/InsightCard.vue'
import Select2 from '@/components/shares/Select2.vue'

import { COMPANY_STATUS, COMPANY_STATUS_FILTER_TABLE } from '@/utils/companyStatus'

import { InfoIcon, AlertTriangleIcon, FilterIcon, ChevronDownIcon, ChevronUpIcon } from 'vue-feather-icons'

const futureDate = days => {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toISOString().split('T')[0]
}

const today = new Date().toISOString().split('T')[0]
const thirtyDaysBefore = futureDate(-30)

const listStatusCards = [
  [200, 'VENCIDAS', 'red'],
  [100, 'EM ABERTO', 'yellow'],
]

export default {
  created() {
    this.initializeData()
  },
  components: {
    Companies,
    FutureAlert, AlertBannerRecords, Card, InsightCard, Select2,
    CmEmbarcadorAutuadorChart, CmEmbarcadorDescricaoChart, CmEmbarcadorEstadoChart, CmEmbarcadorRiskMatrixChart, CmEmbarcadorTimelineChart,
    InfoIcon, AlertTriangleIcon, FilterIcon, ChevronDownIcon, ChevronUpIcon
  },
  computed: {
    labelValueDescription() {
      return 'Valor da Multa: valor original da infração informado pelo órgão autuador.\n' +
        'Alguns órgãos como ANTT podem não fornecer este valor.\n' +
        'Valor do Boleto: valor atualizado presente no boleto de pagamento.'
    },
    lebelTypePeriod() {
      return 'Alguns órgãos podem não fornecer a data da ocorrência e estas multas não serão incluídas nos gráficos ao pesquisar por este tipo de período.\n' +
        'Para garantir a visualização de todas as multas, acesse o aplicativo e faça uma busca pelo status da multa'
    },
    appLink() {
      // Vai sempre, mesmo vazio: é assim que "Todas" chega na consulta, que de
      // outro modo reabriria no default dela (Ativa).
      return `/cm-embarcador?q[company_status]=${encodeURIComponent(this.companyStatus)}`
    },
    queryDate() {
      const query = {
        start_date: this.startDate,
        end_date: this.endDate && `${this.endDate} 23:59:59.999`,
        period_type: this.periodType
      }

      if (this.companyStatus) {
        query.company_status = this.companyStatus
      }

      if (this.selectedStatus.length > 0 && !this.selectedStatus.includes('all')) {
        query.status = this.selectedStatus
      }

      if (this.selectedAutuador.length > 0) {
        query.autuador = this.selectedAutuador
      }

      if (this.selectedEstados.length > 0) {
        query.uf = this.selectedEstados
      }

      return query
    },
    autuadorOptions() {
      return this.IssuingAuthority.table()
    },
    topAutuadores() {
      const allOptions = this.IssuingAuthority.table()
      return allOptions.slice(0, 10)
    },
    availableStatusOptions() {
      return this.availableStatus.map(status => ({
        label: status.label,
        value: status.value
      }))
    },
    autuadorOptionsForSelect() {
      return [
        { label: 'DNIT', value: '000300'  },
        { label: 'ANTT', value: '000400' },
        { label: 'DER/PR', value: '116200' },
        { label: 'DER/SP', value: '126200' },
      ]
    },
    estadosOptions() {
      return this.estadosBrasileiros.map(uf => ({
        label: uf,
        value: uf
      }))
    },
  },
  data: () => ({
    filterKey: '',
    isLoadingData: false,
    debounceTimer: null,
    showAdvancedFilters: false,
    selectedStatus: [],
    selectedAutuador: [],
    selectedEstados: [],
    availableStatus: [
      { value: 'all', label: 'Todas' },
      { value: '100', label: 'Em Aberto' },
      { value: '200', label: 'Vencido' }
    ],
    estadosBrasileiros: [
      'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
      'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
      'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
    ],
    futureAlertData: {
      count: 0,
      sum: 0,
      loading: true,
      days: 7,
      options: [
        { value: 7, label: '7 dias' },
        { value: 15, label: '15 dias' },
        { value: 30, label: '30 dias' },
      ]
    },
    statusCardsData: {
      list: [{}, {}, {}],
      loading: true,
    },
    situationChartData: {
      list: [],
      loading: true,
    },
    autuadorChartData: {
      list: [],
      loading: true,
    },
    estadoChartData: {
      list: [],
      loading: true,
    },
    riskMatrixData: {
      list: [],
      loading: true,
    },
    timelineChartData: {
      list: [],
      loading: true,
    },
    topCnpjsData: {
      list: [],
      loading: true,
    },
    insightsData: {
      avgValue: 0,
      topAutuador: '-',
      topAutuadorCount: 0,
      topEstado: '-',
      topEstadoCount: 0,
      loading: true,
    },
    startDate: thirtyDaysBefore,
    endDate: today,
    periodType: 'updated_at',
    valueType: 'valor_estimado_cents',
    companyStatus: String(COMPANY_STATUS.ENABLED),
    companyStatuses: COMPANY_STATUS_FILTER_TABLE,
    companiesMessages: 'Ainda não existem empresas cadastrados no sistema. <a href="/definitions?tab=companies">Clique aqui</a> para ir para o cadastro de empresas.',
    IssuingAuthority: require('@/models/IssuingAuthority').IssuingAuthority,
  }),
  methods: {
    toggleFilters() {
      this.showAdvancedFilters = !this.showAdvancedFilters
    },
    clearFilters() {
      this.selectedStatus = []
      this.selectedAutuador = []
      this.selectedEstados = []
      this.startDate = thirtyDaysBefore
      this.endDate = today
      this.periodType = 'updated_at'
      this.valueType = 'valor_estimado_cents'
      this.companyStatus = String(COMPANY_STATUS.ENABLED)
      this.initializeData()
    },
    applyFilters() {
      this.initializeData()
    },
    async initializeData(excludeFutureAlert = false) {
      if (this.isLoadingData) {
        return
      }

      this.isLoadingData = true

      const methods = [
        this.loadFutureAlert,
        this.loadStatusCards,
        this.loadClassification,
        this.loadAutuadorAggrregation,
        this.loadEstadoAggregation,
        this.loadRiskMatrixData,
        this.loadTimelineData,
        this.loadTopCNPJs,
      ]

      if (excludeFutureAlert)
        methods.shift()

      for (const method of methods)
        await method()

      this.calculateInsights()
      this.isLoadingData = false
    },
    loadFutureAlert() {
      let data = {}
      const params = {
        aggregations: [{ method: 'sum', field: 'boleto.valor_cents' }],
        fields: ['status'],
        query: {
          status: ['100', '200'],
          period_type: 'boleto_vencimento_em',
          start_date: today,
          end_date: futureDate(this.futureAlertData.days + 1),
          ...(this.companyStatus ? { company_status: this.companyStatus } : {})
        },
        per_page: 1
      }

      const key = 'cm-embarcador/dashboard/futureAlert' + JSON.stringify(params)
      data = this.$ls.get(key)
      if (data) {
        this.futureAlertData.count = data.count
        this.futureAlertData.sum = data.sum
        this.futureAlertData.loading = false
        return
      }

      this.futureAlertData.loading = true
      return this.axios.v3.get('cm-embarcador/multas/aggregations', { params })
        .then(response => {
          if (response.data.length > 0)
            data = { count: response.data[0].count, sum: response.data[0].sum }
          else
            data = { count: 0, sum: 0 }

          this.futureAlertData.count = data.count
          this.futureAlertData.sum = data.sum
          this.$ls.set(key, data, { minutes: 5 })
        })
        .catch(e => this.$notify.addError(e))
        .finally(() => this.futureAlertData.loading = false)
    },
    loadClassification() {
      return this.loadAggregation('descricao', this.situationChartData)
    },
    loadAutuadorAggrregation() {
      return this.loadAggregation('autuador', this.autuadorChartData)
    },
    loadEstadoAggregation() {
      const params = { q: { ...this.queryDate } }
      const key = 'cm-embarcador/dashboard/infractionsMap' + JSON.stringify(params)

      const cached = this.$ls.get(key)
      if (cached) {
        this.estadoChartData.list = cached
        this.estadoChartData.loading = false
        return Promise.resolve()
      }

      this.estadoChartData.loading = true
      return this.axios.v3.get('cm-embarcador/multas/infractions_map', { params, timeout: 30000 })
        .then(response => {
          const data = Array.isArray(response.data) ? response.data : (response.data.data || [])
          const list = data.map(item => ({
            uf: item.uf,
            name: item.name,
            count: item.quantidade,
            sum: item.valor_cents
          }))
          this.estadoChartData.list = list
          this.$ls.set(key, list, { minutes: 5 })
        })
        .catch(e => {
          this.$notify.addError(e)
          this.estadoChartData.list = []
        })
        .finally(() => this.estadoChartData.loading = false)
    },
    loadTimelineData() {
      return this.loadAggregation('ocorrido_em', this.timelineChartData, 1000)
        .then(() => {
          const monthlyData = {}

          this.timelineChartData.list.forEach(item => {
            if (!item.ocorrido_em) return

            const date = new Date(item.ocorrido_em)
            if (isNaN(date.getTime())) return

            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

            if (!monthlyData[monthKey]) {
              monthlyData[monthKey] = { month: monthKey, count: 0, sum: 0 }
            }
            monthlyData[monthKey].count += item.count || 0
            monthlyData[monthKey].sum += item.sum || 0
          })

          this.timelineChartData.list = Object.values(monthlyData)
        })
    },
    loadRiskMatrixData() {
      return this.loadAggregation('cnpj', this.riskMatrixData, 20)
        .then(async () => {
          const cnpjs = this.riskMatrixData.list.map(c => c.cnpj).sort()
          if (cnpjs.length === 0) return

          const companies = await this.findCompanies(cnpjs)
          this.riskMatrixData.list.forEach(c => {
            const company = companies.find(co => co.cnpj === c.cnpj)
            this.$set(c, 'name', company ? company.name : null)
          })
        })
    },
    loadTopCNPJs() {
      return this.loadAggregation('cnpj', this.topCnpjsData, 5)
        .then(async () => {
          const cnpjs = this.topCnpjsData.list.map(c => c.cnpj).sort()
          if (cnpjs.length === 0) return

          const companies = await this.findCompanies(cnpjs)
          this.topCnpjsData.list.forEach(c => {
            const company = companies.find(co => co.cnpj === c.cnpj)
            this.$set(c, 'name', company ? company.name : '-')
          })
        })
    },
    async findCompanies(cnpjs) {
      let companies = this.$ls.get('cm-embarcador/dashboard/companies')
      if (companies) return companies

      const response = await this.axios.get('companies', { params: { q: { cnpj: cnpjs }, type: ['parent', 'subsidiary'] } })
      companies = response.data
      this.$ls.set('cm-embarcador/dashboard/companies', companies, { minutes: 5 })
      return companies
    },
    loadStatusCards() {
      return this.loadAggregation('status', this.statusCardsData)
        .then(() => {
          const data = this.statusCardsData.list
          this.statusCardsData.list = []

          listStatusCards.forEach(([status, title, color]) => {
            const item = data.find(d => d.status === status) || { count: 0, sum: 0 }
            this.statusCardsData.list.push({ ...item, title, color, link: this.buildStatusLink(status) })
          })

          this.statusCardsData.list.push({
            title: 'TOTAL',
            count: data.map(d => d.count).reduce((a, b) => a + b, 0),
            sum: data.map(d => d.sum).reduce((a, b) => a + b, 0),
            color: 'green',
            link: this.buildStatusLink()
          })
        })
    },
    loadAggregation(field, objectData, limit = 100) {
      const params = {
        q: { ...this.queryDate },
        aggregations: [{ method: 'sum', field: this.valueType, order: 'desc' }],
        fields: [field],
        per_page: Math.min(limit, 1000)
      }

      const key = `cm-embarcador/dashboard/${field}` + JSON.stringify(params)
      const cachedData = this.$ls.get(key)
      if (cachedData) {
        objectData.list = cachedData
        objectData.loading = false
        return Promise.resolve()
      }

      objectData.loading = true
      return this.axios.v3.get('cm-embarcador/multas/aggregations', {
        params,
        timeout: 30000
      })
        .then(response => {
          objectData.list = response.data
          this.$ls.set(key, objectData.list, { minutes: 5 })
        })
        .catch(e => {
          this.$notify.addError(e)
          objectData.list = []
        })
        .finally(() => objectData.loading = false)
    },
    buildStatusLink(status) {
      const periodType = this.queryDate.period_type
      const startDate = this.queryDate.start_date
      const endDate = this.queryDate.end_date
      let link = `/cm-embarcador?o[]=updated_at&o[]=desc&q[period_type]=${periodType}&q[start_date]=${startDate}&q[end_date]=${endDate}`
      if (status)
        link += `&q[status]=${status}`

      link += `&q[company_status]=${encodeURIComponent(this.companyStatus)}`

      return link
    },
    calculateInsights() {
      this.insightsData.loading = true

      // O card TOTAL ja e a soma de todos os status; somar a lista inteira
      // contava VENCIDAS e EM ABERTO duas vezes e inflava a media.
      const total = this.statusCardsData.list.find(item => item.title === 'TOTAL') || { count: 0, sum: 0 }

      this.insightsData.avgValue = total.count > 0 ? total.sum / total.count : 0

      if (this.autuadorChartData.list.length > 0) {
        const sortedAutuadores = [...this.autuadorChartData.list].sort((a, b) => b.count - a.count)
        const topAutuador = sortedAutuadores[0]
        this.insightsData.topAutuador = require('@/models/IssuingAuthority').IssuingAuthority.label(topAutuador.autuador) || '-'
        this.insightsData.topAutuadorCount = topAutuador.count || 0
      }

      if (this.estadoChartData.list.length > 0) {
        const sortedEstados = [...this.estadoChartData.list].sort((a, b) => b.count - a.count)
        const topEstado = sortedEstados[0]
        this.insightsData.topEstado = topEstado.uf || '-'
        this.insightsData.topEstadoCount = topEstado.count || 0
      }

      this.insightsData.loading = false
    }
  },
  mixins: [GetAccount, GetAccountApps],
  name: 'DashboardCMEmbarcador',
  watch: {
    'futureAlertData.days'() {
      this.loadFutureAlert()
    }
  }
}
</script>

<style scoped>
.filters-card {
  background-color: var(--white-bg);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>

<style>
.tooltip-with-linebreaks .tooltip-content {
  white-space: pre-line;
}
</style>
